import { useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowUpwardOutlined } from '@mui/icons-material';
import { Alert, AlertTitle, Box, Button, CircularProgress, List, ListItem, ListItemText } from '@mui/material';
import { TextAreaFieldWithController } from '@procureai/ui-form-mui';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { ReactNode, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';

import { SvbrgChatMessageStatus, SvbrgLanguage } from '@/generated/gql/graphql';
import { ChatLoadingIndicator } from '@/legacy/components/Chat/ChatMessage/indicators/ChatLoadingIndicator';
import { I18nNamespace } from '~/common/types';
import { ChatMessageProps } from '~/common/types/ChatMessage';
import { ChatStep } from '~/components/ChatStep/ChatStep';
import { GENERATE_STATEMENT_OF_WORK_TOOL, useToolCall } from '~/hooks';
import { useChatSession } from '~/modules';

import {
  INTAKE_STATEMENT_OF_WORK_NEXT_MUTATION,
  INTAKE_STATEMENT_OF_WORK_UPSERT_MUTATION,
} from './IntakeGenerateStatementOfWorkStep.mutation.gql';
import { INTAKE_STATEMENTS_OF_WORK } from './IntakeGenerateStatementOfWorkStep.query.gql';
import {
  IntakeGenerateStatementOfWorkStepFormData,
  intakeGenerateStatementOfWorkStepFormDataDefault,
  intakeGenerateStatementOfWorkStepFormDataSchema,
} from './IntakeGenerateStatementOfWorkStep.schema';

export type IntakeGenerateStatementOfWorkStepProps = ChatMessageProps;

/**
 * DISCLAIMER: This is part a POC to generate statement of work drafts based on previous intake steps.
 * It is not production ready and should not be used as is.
 * It is hidden behind a feature flag and not part of the standard intake flow.
 * It will be further developed and improved in future iterations.
 */
export const IntakeGenerateStatementOfWorkStep = (props: IntakeGenerateStatementOfWorkStepProps): ReactNode => {
  const { createdBy, status, isCompleted, role } = props;

  const session = useChatSession();
  const { onError } = useCustomSnackbar();

  const { t: tGlobal, i18n } = useTranslation(I18nNamespace.intake);
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeGenerateStatementOfWorkStep' });

  const [missingSections, setMissingSesctions] = useState<{ section: string; information: string }[]>([]);

  const form = useForm<IntakeGenerateStatementOfWorkStepFormData>({
    mode: 'onTouched',
    resolver: zodResolver(intakeGenerateStatementOfWorkStepFormDataSchema),
    defaultValues: intakeGenerateStatementOfWorkStepFormDataDefault,
  });

  const { setValue, reset } = form;

  const { data, loading } = useQuery(INTAKE_STATEMENTS_OF_WORK, {
    variables: { id: session.id },
  });

  const requestLanguage = data?.sevenbridges.chatSession.requestDescription?.language;

  // update language if i18n language changes
  const i18nLanguage = i18n.language;
  useEffect(() => {
    const userLanguage =
      !i18nLanguage || i18nLanguage === 'dx' ? SvbrgLanguage.DE : (i18nLanguage.toUpperCase() as SvbrgLanguage);

    // we either use the request language or the user language as fallback
    setValue('language', requestLanguage || userLanguage);
  }, [i18nLanguage, requestLanguage, setValue]);

  const [save] = useMutation(INTAKE_STATEMENT_OF_WORK_UPSERT_MUTATION, {
    onError,
  });

  const [goToNext] = useMutation(INTAKE_STATEMENT_OF_WORK_NEXT_MUTATION, {
    onError,
  });

  const { mutate: generateSow, isLoading: isGenerating } = useToolCall(GENERATE_STATEMENT_OF_WORK_TOOL);

  const currentSows = data?.sevenbridges.chatSession.statementsOfWork;

  const language = form.watch('language');

  // use effect to set the form value when the data is loaded
  useEffect(() => {
    if (!loading && currentSows && currentSows.length > 0) {
      // for now we just take the first statement of work as we do not have support multiple languages yet
      const sow = currentSows[0];

      // reset the form
      reset({ statementOfWork: sow.text, language: sow.language as SvbrgLanguage });
    }
  }, [currentSows, loading, reset]);

  // use effect to generate the statement of work, if it is initially empty
  useEffect(() => {
    // only care for loaded proceed states
    if (loading || !session.id || status !== SvbrgChatMessageStatus.PROCEED) return;

    // for now we just take the first statement of work as we do not have support multiple languages yet
    const sowText = currentSows?.[0]?.text;

    // if we do not have a text yet and it is empty, let's generate it
    if (!sowText) {
      setMissingSesctions([]);
      generateSow({ chatSessionId: session.id, language })
        .then((result) => {
          setMissingSesctions(result.missingSections ?? []);
          setValue('statementOfWork', result.statementOfWork, { shouldValidate: true });
        })
        .catch(onError);
    }
  }, [session.id, status, currentSows, loading, setValue, onError, generateSow, language]);

  return (
    <ChatStep
      title={tGlobal('IntakeChatStep.assistant')}
      role={role}
      createdBy={createdBy}
      message={!isGenerating && t('completedMessage')}
    >
      <Box>
        {loading && <CircularProgress />}

        {isCompleted && <Markdown>{form.getValues().statementOfWork}</Markdown>}

        {isGenerating && <ChatLoadingIndicator message={t('generatingMessage')} />}

        {!isGenerating && !isCompleted && (
          <FormProvider {...form}>
            <form
              noValidate
              autoComplete="off"
              onSubmit={form.handleSubmit(async (data) => {
                // save the current statement of work
                const result = await save({
                  variables: {
                    data: {
                      chatSessionId: session.id,
                      language: data.language,
                      text: data.statementOfWork,
                    },
                  },
                });

                if (result.data?.sevenbridges) {
                  // right now we do auto proceed to the next step after saving the statement of work
                  // but only if we have a valid id in the response
                  await goToNext({
                    variables: {
                      data: {
                        chatSessionId: session.id,
                      },
                    },
                  });
                }
              })}
            >
              {missingSections.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <AlertTitle>{t('form.missingAlertTitle')}</AlertTitle>
                  <List>
                    {missingSections.map((m, idx) => (
                      <ListItem dense={true} disableGutters={true} key={idx}>
                        <ListItemText primary={m.section} secondary={m.information} />
                      </ListItem>
                    ))}
                  </List>
                </Alert>
              )}

              <TextAreaFieldWithController
                rows={10}
                name={'statementOfWork'}
                fullWidth
                variant="outlined"
                placeholder={t('form.placeholder')}
              />

              <Button
                sx={{ mt: 2 }}
                variant="contained"
                color="primary"
                type="submit"
                disabled={!form.formState.isValid}
                loading={form.formState.isSubmitting}
                startIcon={<ArrowUpwardOutlined />}
              >
                {t('form.submit')}
              </Button>
            </form>
          </FormProvider>
        )}
      </Box>
    </ChatStep>
  );
};
